import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const LAYERS = ["l3", "l4", "l7"];
const ACTIONS = ["allow", "deny", "log"];
const SUBJECTS = ["user", "group", "service_account", "device"];
const empty = {
  name: "", layer: "l7", action: "deny",
  subject_type: "user", subject_id: "",
  url_category_ids: [], dlp_rule_ids: [], sensitivity_min: "",
  time_window: "", priority: 100, enabled: true, description: "",
};

export default function IdentityPolicies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/identity-policies"); setItems(data); }
    catch (err) { console.error("identity load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.subject_id) delete payload.subject_id;
      if (!payload.sensitivity_min) delete payload.sensitivity_min;
      if (!payload.time_window) delete payload.time_window;
      await api.post("/identity-policies", payload);
      toast.success("Policy created"); setOpen(false); setForm(empty); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this identity policy?")) return;
    try { await api.delete(`/identity-policies/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="Identity Policies" subtitle="ZERO-TRUST">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="ipol-count">
          {items.length} POLICIES · {items.filter((i) => i.enabled).length} ENABLED
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-ipol-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW POLICY</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-400" /> Create Identity Policy</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="ipol-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Layer</L><Sel value={form.layer} onChange={(v) => setForm({ ...form, layer: v })} options={LAYERS} testid="ipol-layer-select" /></div>
              <div><L>Action</L><Sel value={form.action} onChange={(v) => setForm({ ...form, action: v })} options={ACTIONS} testid="ipol-action-select" /></div>
              <div><L>Subject type</L><Sel value={form.subject_type} onChange={(v) => setForm({ ...form, subject_type: v })} options={SUBJECTS} testid="ipol-subject-select" /></div>
              <div><L>Subject id (optional)</L><Input data-testid="ipol-subjid-input" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Priority</L><Input data-testid="ipol-prio-input" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Sensitivity min</L><Input data-testid="ipol-sens-input" placeholder="confidential" value={form.sensitivity_min} onChange={(e) => setForm({ ...form, sensitivity_min: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Time window (optional)</L><Input data-testid="ipol-time-input" placeholder="MON-FRI 09:00-18:00 UTC" value={form.time_window} onChange={(e) => setForm({ ...form, time_window: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Description</L><Input data-testid="ipol-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div className="col-span-2 flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Enabled</span>
                <Switch data-testid="ipol-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-ipol-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ipol-list">
        {items.map((p) => (
          <div key={p.policy_id} data-testid={`ipol-card-${p.policy_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight">{p.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{p.policy_id} · prio {p.priority}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " +
                (p.action === "deny" ? "text-red-400 border-red-500/40 bg-red-500/10"
                  : p.action === "allow" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                  : "text-cyan-400 border-cyan-500/40 bg-cyan-500/10")}>{p.action} · {p.layer}</span>
            </div>
            <div className="font-mono text-xs space-y-1 text-zinc-300">
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">SUBJECT</span>{p.subject_type}{p.subject_id ? ` / ${p.subject_id}` : " / *"}</div>
              {p.sensitivity_min && <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">SENS≥</span><span className="text-amber-400">{p.sensitivity_min}</span></div>}
              {p.time_window && <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">WHEN</span>{p.time_window}</div>}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className={"text-[10px] font-mono uppercase tracking-widest " + (p.enabled ? "text-emerald-400" : "text-zinc-500")}>{p.enabled ? "ENABLED" : "DISABLED"}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-ipol-${p.policy_id}`} onClick={() => del(p.policy_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no identity policies yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
function Sel({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">
        {options.map((o) => <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
